import type { ButtonProps } from '@mui/material/Button';
import type { DialogProps } from '@mui/material/Dialog';
import { useCallback, useId, useMemo, useState } from 'react';

export interface UseDisclosureProps {
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  id?: string;
}

/**
 * `useDisclosure` is a custom hook used to help handle common open, close, or toggle scenarios.
 * It can be used to control feedback component such as `Modal`, `AlertDialog`, `Drawer`, etc.
 */
export function useDisclosure(props: UseDisclosureProps = {}) {
  const { isOpen: isOpenProp, id: idProp } = props;

  const [isOpenState, setIsOpen] = useState(props.defaultIsOpen || false);

  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenState;

  const isControlled = isOpenProp !== undefined;

  const uid = useId();
  const id = idProp ?? `disclosure-${uid}`;

  const onClose = useCallback(() => {
    if (!isControlled) {
      setIsOpen(false);
    }
  }, [isControlled, setIsOpen]);

  const onOpen = useCallback(() => {
    if (!isControlled) {
      setIsOpen(true);
    }
  }, [isControlled, setIsOpen]);

  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);

  const getButtonProps = useMemo(
    () =>
      (additionalProps: ButtonProps = {}): ButtonProps => ({
        ...additionalProps,
        'aria-expanded': isOpen,
        'aria-controls': id,
        onClick(event) {
          event.preventDefault();
          event.stopPropagation();
          additionalProps.onClick?.(event);
          onToggle();
        },
      }),
    [isOpen, id, onToggle],
  );

  const getDisclosureProps = useMemo(
    () =>
      (additionalProps: Omit<DialogProps, 'open'> = {}) => ({
        ...additionalProps,
        open: isOpen,
        onClose: () => {
          onClose();
          additionalProps?.onClose?.({}, 'backdropClick');
        },
        id,
      }),
    [isOpen, id, onClose],
  );

  return {
    id,
    isOpen,
    onOpen,
    onClose,
    onToggle,
    isControlled,
    getButtonProps,
    getDisclosureProps,
  };
}

export type UseDisclosureReturn = ReturnType<typeof useDisclosure>;
